import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { Input as CoreInput } from 'core-components/library/inputs'
import { RemoveButton } from 'core-components/library/multiSelect'
import { ValidateInput as CoreValidateInput, isEmpty } from 'core-components/library/validation'

import { validateRange } from './calendar/helpers/validateRange'
import { DEFAULT_DATE_MASK } from './constants'
import { convertDateToString } from './helpers/convertDateToString'
import { splitDisplayDate } from './helpers/splitDisplayDate'
import { validateIsFullDateFilled } from './helpers/validateIsFullDateFilled'
import { IDatePickerInput } from './models/IDatePickerInput'

import styles from './Input.module.scss'

export const Input = ({
  value,
  onChange,
  classes,
  mode,
  label,
  minutesStep,
  disabled,
  dateFormat,
  required,
  isPopupInput,
  clearButtonVisible,
  toDate,
  fromDate,
  disableEmptyPeriod = false,
}: IDatePickerInput) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const getDisplayValue = useCallback(
    () => convertDateToString(value, isPopupInput, dateFormat, !!minutesStep),
    [dateFormat, isPopupInput, minutesStep, value]
  )
  const [displayValue, setDisplayValue] = useState<string>(getDisplayValue)
  const removeButtonVisible = useMemo(() => {
    if (clearButtonVisible) {
      if (isPopupInput) {
        return displayValue
      }
    }
    return false
  }, [isPopupInput, displayValue, clearButtonVisible])
  const isTypingAllowed = useMemo(() => {
    return isPopupInput && mode === 'single' && !dateFormat
  }, [isPopupInput, mode, dateFormat])

  const clearValue = () => {
    setDisplayValue('')
    // TODO: onChange event must send Date | undefined
    // @ts-ignore
    onChange(undefined)
  }

  useEffect(() => {
    setDisplayValue(getDisplayValue())
  }, [value, dateFormat, minutesStep, getDisplayValue])

  useEffect(() => {
    if (!isPopupInput) {
      return
    }
    const focusFrame = requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
    return () => {
      cancelAnimationFrame(focusFrame)
    }
  }, [isPopupInput])

  const iconContent = removeButtonVisible ? (
    <RemoveButton disabled={disabled} onClick={clearValue} />
  ) : (
    <div
      data-testid="datepicker-input-calendar-icon"
      onClick={() => inputRef.current?.focus()}
      className={classNames('kbase kbase-calendar', styles.icon)}
    />
  )

  const inputProps = {
    ref: inputRef,
    label,
    disabled,
    value: displayValue,
    placeholder: DEFAULT_DATE_MASK.toUpperCase(),
    readOnly: !isTypingAllowed,
    classes: {
      ...classes?.inputClasses,
      root: classNames(styles.root, classes?.inputClasses?.root),
      control: classNames(classes?.inputClasses?.control, {
        [styles['control--readOnly']]: !isTypingAllowed,
      }),
    },
    onBlur: () => {
      if (!isPopupInput) {
        return
      }
      if (!displayValue && clearButtonVisible) {
        clearValue()
        return
      }
      if (isTypingAllowed) {
        const validDate = validateRange(splitDisplayDate(displayValue), fromDate, toDate)
        if (!validDate) {
          setDisplayValue(getDisplayValue())
        }
      }
    },
    onChange: (newValue: string) => {
      if (isTypingAllowed && mode === 'single') {
        const newDate = splitDisplayDate(newValue)
        if (newDate) {
          const validDate = validateRange(newDate, fromDate, toDate)
          if (validDate) {
            onChange(validDate)
          }
        }
        setDisplayValue(newValue)
      }
    },
    endContent: iconContent,
  }

  const checkFn = useCallback(() => {
    if (disableEmptyPeriod) {
      return validateIsFullDateFilled(value as DateRange)
    } else {
      return isEmpty(inputProps.value)
    }
  }, [value, inputProps.value])

  if (disableEmptyPeriod || required) {
    return (
      <CoreValidateInput
        {...inputProps}
        checkFn={checkFn}
        classes={{
          inputClasses: inputProps.classes,
          validatorClasses: classes?.validatorClasses,
        }}
      />
    )
  }

  return <CoreInput {...inputProps} />
}
